import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import TodoCardWrapper from './TodoCardWrapper';
import TakeLabel from '../../../../components/label/TakeLabel';
import Counter from '../../../../components/counter/Counter';
import DateDisplay, { DATE_DISPLAY_TYPE } from '../../../../components/date-display/DateDisplay';
import Button from '../../../../components/button/Button';
import DropdownMenu from '../../../../components/dropdown-menu/DropdownMenu';
import MediaPreview from '../../../../components/media-preview/MediaPreview';
import MessageItem from '../../../../components/message/MessageItem';
import Modal from '../../../../components/modal/Modal';
import DateInput from '../../../../components/calendar/DateInput';
import { CircularFab } from '../../../../components/fab';
import { Shot, getShotPlaybackSrc, useUpdateShotStatusAnimatedMutation, useUpdateShotMutation, hasValidDeliveryDate } from '../../../../app/services/shots';
import { ShotStatus } from '../../../../types/home';
import { useGetShotCommentsQuery } from '../../../../app/services/shotComments';
import { useGetMediaItemsQuery } from '../../../../app/services/mediaItems';
import { shotCommentToMessageData } from '../../../../utils/messageAdapters';
import { isClientGroupPosition } from '../../../../utils/memberPositionUtils';
import useShotApproval from '../../../../hooks/useShotApproval';
import useAnimatedStatusChange from '../../../../hooks/useAnimatedStatusChange';

interface TodoShotCardProps {
  shot: Shot;
  index: number;
  project_user_position: string;
  ShotStatus: typeof ShotStatus;
  onShotStatusChanged?: () => void;
}

const TodoShotCard: React.FC<TodoShotCardProps> = ({ 
  shot, 
  index, 
  project_user_position, 
  ShotStatus,
  onShotStatusChanged,
}) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { isMobile } = useSelector((state: any) => state.device);
  
  const shotId = shot.id;

  const [updateShotStatus] = useUpdateShotStatusAnimatedMutation();
  const [updateShot] = useUpdateShotMutation();

  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(() => {
    if (shot.next_delivery_date) return new Date(shot.next_delivery_date);
    const d = new Date();
    d.setHours(10, 0, 0, 0);
    return d;
  });

  const showDeliveryDate = useMemo(() => hasValidDeliveryDate(shot), [shot.next_delivery_date]);

  const { fadingOut, executeWithAnimation } = useAnimatedStatusChange({
    onComplete: onShotStatusChanged,
  });

  // 検収ON/OFF（共通hook — onStatusChanged はアニメーション側で制御）
  const { isApproved, canApprove, isLoading: isApprovalLoading, toggleApproval } = useShotApproval({
    projectId: projectId || '',
    shotId: shotId || '',
    currentStatus: shot.status,
    onStatusChanged: onShotStatusChanged,
    skipOnStatusChanged: true,
    useAnimatedMutation: true,
  });

  // プレビューページへの遷移ハンドラ
  const handlePreviewClick = useCallback(() => {
    if (projectId && shotId) {
      navigate(`/${projectId}/${shotId}`, {
        state: { shot }
      });
    }
  }, [navigate, projectId, shotId, shot]);

  // メディアアイテム追加（ShotEditModal）を開いた状態でプレビューページへ遷移
  const handleUpdateClick = useCallback(() => {
    if (projectId && shotId) {
      navigate(`/${projectId}/${shotId}`, {
        state: { shot, openShotEditModal: true }
      });
    }
  }, [navigate, projectId, shotId, shot]);

  // 提出予定日を保存
  const handleSaveDeliveryDate = useCallback(async () => {
    if (!projectId || !shotId || !deliveryDate) return;
    try {
      const y = deliveryDate.getFullYear();
      const m = String(deliveryDate.getMonth() + 1).padStart(2, '0');
      const d = String(deliveryDate.getDate()).padStart(2, '0');
      const h = String(deliveryDate.getHours()).padStart(2, '0');
      const min = String(deliveryDate.getMinutes()).padStart(2, '0');
      await updateShot({
        project_id: projectId,
        shot_id: shotId,
        next_delivery_date: `${y}-${m}-${d}T${h}:${min}:00Z`,
      }).unwrap();
      setShowDeliveryDateModal(false);
      onShotStatusChanged?.();
    } catch (error) {
      console.error('提出予定日の更新エラー:', error);
    }
  }, [projectId, shotId, deliveryDate, updateShot, onShotStatusChanged]);

  // ステータスをCLIENT_PENDINGに変更（確認依頼）
  const handleClientPending = useCallback(() => {
    if (!projectId || !shotId) return;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: ShotStatus.CLIENT_PENDING,
      }).unwrap()
    );
  }, [projectId, shotId, updateShotStatus, ShotStatus, executeWithAnimation]);

  // ステータスを保留に変更
  const handleSetHold = useCallback(() => {
    if (!projectId || !shotId) return;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED,
      }).unwrap()
    );
  }, [projectId, shotId, updateShotStatus, ShotStatus, executeWithAnimation]);

  // 保留を取り消し、操作ユーザー側のToDoステータスへ戻す
  const handleCancelHold = useCallback(() => {
    if (!projectId || !shotId) return;
    const revertStatus = isClientGroupPosition(project_user_position)
      ? ShotStatus.CLIENT_PENDING
      : ShotStatus.PRODUCER_PENDING;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: revertStatus,
      }).unwrap()
    );
  }, [projectId, shotId, updateShotStatus, ShotStatus, project_user_position, executeWithAnimation]);

  // 検収トグル（検収ON時はマイクロインタラクション 1500ms 完了後にフェードアウト）
  const handleToggleApproval = useCallback(() => {
    if (!isApproved) {
      window.dispatchEvent(new CustomEvent("approval-start"));
    }
    const delay = isApproved ? 0 : 1500;
    executeWithAnimation(() => toggleApproval(), { delay });
  }, [isApproved, toggleApproval, executeWithAnimation]);

  // 最新の未解決コメントを取得
  const { data: commentsData } = useGetShotCommentsQuery(
    {
      shot_id: shotId,
      filter: 'latest_unresolved',
      limit: 1,
    },
    {
      skip: !shotId, // IDがない場合はAPIコールをスキップ
    }
  );

  // コメントデータから最新のコメントを抽出
  const latestComment = commentsData?.data?.comments?.[0] || null;

  // status=2,3,4: 最新バリエーションのテイク数を算出するためMediaItemsを取得
  const needsTakeInfo = [ShotStatus.UPDATE, ShotStatus.PRODUCER_PENDING, ShotStatus.CLIENT_PENDING].includes(shot.status);
  const { data: mediaItemsData } = useGetMediaItemsQuery(
    { shot_id: shotId },
    { skip: !shotId || !needsTakeInfo }
  );

  const takeCount = useMemo(() => {
    if (!mediaItemsData?.data?.media_items || !shot.variation_name) return 1;
    const items = mediaItemsData.data.media_items;
    return items.filter(item => item.variation_name === shot.variation_name).length;
  }, [mediaItemsData, shot.variation_name]);

  const isAudio = useMemo(() => {
    const src = shot.media_file || shot.thumbnail || '';
    const ext = src.split('?')[0].split('.').pop()?.toLowerCase();
    return ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac'].includes(ext || '');
  }, [shot.media_file, shot.thumbnail]);

  return (
    <>
      <TodoCardWrapper fadingOut={fadingOut}>
      {shot.status === ShotStatus.NEW && (
        <TakeLabel take={1} />
      )}
      {needsTakeInfo && (
        takeCount >= 2
          ? <TakeLabel take={takeCount} />
          : <TakeLabel label="UPDATE" />
      )}
      <Counter type="todo" number={index + 1} />

      <p className="u-ml40 heading-spacing u-line-height-200" translate="no">{shot.shot_name}</p>
      <div className="u-ml40 bodytext-11 u-text-light-gray u-row">
        {showDeliveryDate ? (
          <DateDisplay date={shot.next_delivery_date!} type={DATE_DISPLAY_TYPE.DEADLINE_COUNTDOWN} />
        ) : (
          <DateDisplay date={shot.updated_at} type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME} />
        )}
      </div>
      <MediaPreview
        src={getShotPlaybackSrc(shot) || shot.thumbnail || undefined}
        thumbnailSrc={shot.thumbnail || undefined}
        sizeMode={{ mode: 'auto', objectFit: 'cover' }}
        autoPlay={false}
        hoverPlay={!isMobile && !isAudio}
        controls={false}
        showStatusBar={false}
        showWaveform={true}
        className="video-wrapper"
        disableHoverEffect={true}
      />
      {latestComment && (
        <div className="u-ptb8 u-border-top u-border-bottom">
          <MessageItem
            message={shotCommentToMessageData(latestComment)}
            isOurs={false}
            isSystem={false}
            showAvatar={true}
            showActions={false}
            compactMediaPreview={true}
          />
        </div>
      )}
      <div className="u-row-between u-mt8 u-items-center">
        {isClientGroupPosition(project_user_position) ? (
          <div className="u-w100 u-row-between u-gap8 u-items-center">
            <Button
              size="small"
              theme="tertiary"
              variant="filled"
              leftIcon="reply"
              width="fit"
              onClick={handlePreviewClick}
            >
              詳しくチェック
            </Button>
            <CircularFab
              isActive={isApproved}
              onClick={handleToggleApproval}
              disabled={!canApprove || isApprovalLoading || fadingOut}
              variant="client"
              size={48}
            />
          </div>
        ) : (
          <div className="u-w100 u-row-between u-gap8 u-items-center">
            <Button
              size="small"
              theme="tertiary"
              variant="filled"
              leftIcon="reply"
              width="fit"
              onClick={handlePreviewClick}
            >
              返信
            </Button>
            <div className="u-row u-gap8 u-items-center">
              <Button
                rightIcon="upload"
                size="small"
                theme="primary"
                variant="filled"
                onClick={handleUpdateClick}
              >
                提出
              </Button>
              <DropdownMenu
                items={[
                  {
                    label: '提出予定日を設定',
                    icon: 'event',
                    onClick: () => setShowDeliveryDateModal(true),
                  },
                  {
                    label: 'クライアントタスクにする',
                    icon: 'shortcut',
                    onClick: handleClientPending,
                  },
                  ...(shot.status === ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED
                    ? [{
                        label: '保留を取り消す',
                        icon: 'undo',
                        onClick: handleCancelHold,
                      }]
                    : [{
                        label: 'このショットを保留',
                        icon: 'do_not_disturb_on',
                        onClick: handleSetHold,
                      }]),
                ]}
              />
            </div>
          </div>
        )}
      </div>
      </TodoCardWrapper>
      {showDeliveryDateModal && (
        <Modal
          onClose={() => setShowDeliveryDateModal(false)}
          title="提出予定日を設定"
          primaryAction={{
            label: "設定",
            onClick: handleSaveDeliveryDate,
            disabled: !deliveryDate,
          }}
          secondaryAction={{
            label: "キャンセル",
            onClick: () => setShowDeliveryDateModal(false),
          }}
        >
          <DateInput
            label="提出予定日"
            placeholder="日付を選択"
            value={deliveryDate}
            minDate={new Date()}
            dateFormat="Y年n月j日（D）H:i"
            options={{ static: false, enableTime: true, time_24hr: true, defaultHour: 10, defaultMinute: 0 }}
            onChange={(e: { target: { value: Date } }) => setDeliveryDate(e.target.value)}
          />
        </Modal>
      )}
    </>
  );
};

export default TodoShotCard;
